<template>
  <div class="ak-card" :class="classes" @click="select">
    <template v-if="$slots.default">
      <slot />
    </template>
  </div>
</template>

<script>
import { computed, defineComponent, inject, watch, ref } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-card>
   *  <ak-card-body :type="'comfy">
   *    <p>Lorem ipsum dolor.</p>
   *  </ak-card-body>
   * </ak-card>
   */
  name: 'AkCard',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description Boolean flag to determine if the card is selectable
     * or not, this required that the card be nested inside the card-item
     * and the cards components.
     */
    selectable: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Boolean flag to determine if the card is hoverable (scale up effect)
     */
    hoverable: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Boolean flag to determine if the card has an overflow overlay
     * which can be used to show if there is more content to be displayed
     */
    overflowOverlay: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Decide on the type of shadow for the card
     */
    shadow: {
      type: String,
      validator: (v) => ['none', 'dark'].indexOf(v) >= 0,
      default: null,
    },

    /**
     * @type {Mixed}
     * @description The value of the card when it is selected this will
     * be the model value on the cards component if selected.
     */
    value: {
      type: [String, Number, Boolean, Object, Array, Function],
      default: undefined,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const parent = inject('akCards')

    const modelValue = computed(() => {
      return parent && parent.modelValue
    })

    const selected = ref(modelValue.value || false)

    watch(modelValue, (newVal) => {
      selected.value = newVal
    })

    const classes = computed(() => {
      return {
        'ak-card--selectable': props.selectable,
        'is-selected': selected.value === props.value,
        'has-overflow-overlay': props.overflowOverlay,
        'ak-card--hoverable': props.hoverable,
        [`ak-card--shadow-${props.shadow}`]: props.shadow,
      }
    })

    const select = () => {
      if (
        parent &&
        typeof parent.select === 'function' &&
        props.selectable === true
      ) {
        parent.select(props.value)
      }
    }

    return {
      classes,
      selected,
      select,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-card {
  background: #fff;
  box-shadow: $base-card-shadow;
  border-radius: $base-border-radius;
  transition: $transition;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  flex-grow: 1;
  flex-basis: 0;

  &--hoverable {
    &:hover {
      transform: scale(1.05);
    }
  }

  &--shadow-none {
    box-shadow: none;
  }

  &--shadow-dark {
    box-shadow: $base-card-shadow-dark;
  }

  &--selectable {
    border: 2px solid transparent;
  }

  &.is-selected {
    border-color: $color-primary;
  }

  &.has-overflow-overlay {
    position: relative;
    &:after {
      pointer-events: none;
      content: ' ';
      background-image: linear-gradient(
        to top,
        rgba(255, 255, 255, 1) 30%,
        rgba(255, 255, 255, 0)
      );
      height: 100px;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      border-radius: 0 0 $base-border-radius $base-border-radius;
    }
  }
}
</style>
